/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useShellScreenHeaderRefresh } from '../../shell/navigation/TabScreenRefreshContext';
import { useSaleDetail } from '../application/useSaleDetail';
import { useSaleCustomerCollect } from '../application/useSaleCustomerCollect';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useAuthStore } from '../../auth/store/useAuthStore';
import type { SaleDetailRouteParams } from '../application/salesNavigation';
import { SaleDetailContent } from '../components/SaleDetailContent';
import { SaleDetailMpQrModal } from '../components/SaleDetailMpQrModal';
import { VoidSaleConfirmModal } from '../components/VoidSaleConfirmModal';
import { saleDetailStyles as styles } from '../components/saleDetail.styles';

type SaleDetailRoute = RouteProp<{ SaleDetail: SaleDetailRouteParams }, 'SaleDetail'>;

export function SaleDetailScreen() {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const navigation = useNavigation();
  const route = useRoute<SaleDetailRoute>();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const saleId = route.params?.saleId ?? '';

  const handleBranchMismatch = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const {
    detail,
    loading,
    diningTableName,
    paymentMethodNames,
    paymentMethodNamesLoaded,
    canVoid,
    requireSupervisorPinForVoid,
    canCollectMpQr,
    mpQrModalVisible,
    openMpQrModal,
    closeMpQrModal,
    voidLoading,
    voidConfirmVisible,
    customerVoidPlan,
    customerPreviewLoading,
    onVoidPress,
    onVoidConfirm,
    onVoidConfirmClose,
    refresh,
    onMpQrTerminal,
    canReprint,
    reprintLoading,
    onReprintPress,
  } = useSaleDetail({
    saleId,
    branchId,
    onBranchMismatch: handleBranchMismatch,
  });
  const { canCollectCustomer, onCollectCustomer } = useSaleCustomerCollect(detail);

  const hasCommissionsCapability = useHasBusinessCapability('commissions');
  const commissionsPermission = useBranchPermission('Commissions');
  const sessionUser = useAuthStore((s) => s.session?.user ?? null);
  const showCommissions = hasCommissionsCapability && commissionsPermission.canView;
  const showAttribution = hasCommissionsCapability;
  const canViewOthersCommissions = commissionsPermission.canViewOthers;

  const onHeaderRefresh = useCallback(async () => {
    await refresh();
  }, [refresh]);

  useShellScreenHeaderRefresh('SaleDetail', onHeaderRefresh);

  const onViewLinkedOrder = useCallback(() => {
    const orderId = detail?.customerOrderId;
    if (!orderId) return;
    (navigation as { navigate: (name: string, params: { orderId: string }) => void }).navigate(
      'OnlineOrderDetail',
      { orderId }
    );
  }, [detail?.customerOrderId, navigation]);

  return (
    <ShellContentFrame style={[ticketSurfaces.screen, styles.container]}>
      <SaleDetailContent
        loading={loading}
        detail={detail}
        diningTableName={diningTableName}
        paymentMethodNames={paymentMethodNames}
        paymentMethodNamesLoaded={paymentMethodNamesLoaded}
        canVoid={canVoid}
        voidLoading={voidLoading}
        onVoidPress={onVoidPress}
        canCollectMpQr={canCollectMpQr}
        onCollectMpQr={openMpQrModal}
        canCollectCustomer={canCollectCustomer}
        onCollectCustomer={onCollectCustomer}
        onViewLinkedOrder={detail?.customerOrderId ? onViewLinkedOrder : undefined}
        canReprint={canReprint}
        reprintLoading={reprintLoading}
        onReprintPress={onReprintPress}
        onFiscalRetryComplete={() => void refresh()}
        onClose={() => {
          if (navigation.canGoBack()) {
            navigation.goBack();
          }
        }}
        footerMode="sticky"
        refreshing={loading}
        onRefresh={() => void onHeaderRefresh()}
        showCommissions={showCommissions}
        showAttribution={showAttribution}
        currentUserId={sessionUser?.id ?? null}
        canViewOthersCommissions={canViewOthersCommissions}
        onNavigateToCommissionPlans={() => (navigation as any).navigate('CommissionPlans')}
      />
      <SaleDetailMpQrModal
        detail={detail}
        visible={mpQrModalVisible}
        onClose={closeMpQrModal}
        onTerminal={onMpQrTerminal}
        onVoidSale={onVoidPress}
      />
      <VoidSaleConfirmModal
        visible={voidConfirmVisible}
        saleTotal={detail?.total ?? 0}
        loading={voidLoading}
        isFiscalSale={detail?.fiscalMode === 'fiscal'}
        hasMercadoPagoQrPayment={Boolean(detail?.payments.some((p) => p.methodId === 'mercadopago_qr'))}
        hasCommissions={hasCommissionsCapability}
        customerVoidPlan={customerVoidPlan}
        customerPreviewLoading={customerPreviewLoading}
        onClose={onVoidConfirmClose}
        requireSupervisorPin={requireSupervisorPinForVoid}
        onConfirm={(reason, pin) => onVoidConfirm(reason, pin)}
      />
    </ShellContentFrame>
  );
}
